import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TrustBox } from '../trustpilotCarousel/TrustpilotCarousel';
import { fixedImage } from '../../utils';

import fscs from '../../../content/media-library/logos/FSCS-logo.svg';
import finderLogo from '../../../content/media-library/awards/finder-banking-customer-satisfaction-2023.png';
import fairpaymentLogo from '../../../content/media-library/awards/fair-payment-gold-logo.png';
import lucaLogo from '../../../content/media-library/awards/icb-logo-2021.png';
import whichLogo from '../../../content/media-library/awards/which-current-accounts-nov-2024.png';
import nacfbLogo from '../../../content/media-library/logos/NACFB-logo.png';
import cassLogo from '../../../content/media-library/logos/cass-logo.svg';
import startupLogo from '../../../content/media-library/logos/StartUp.png';
import gbeaLogo from '../../../content/media-library/logos/GBEA-logo.png';
import contracting from '../../../content/media-library/awards/contracting-awards.jpg';
import times2023 from '../../../content/media-library/awards/times-money-mentor-best-current-account-2023.svg';
import bestapp2024 from '../../../content/media-library/awards/bba-best-banking-app.png';
import bestchildrensprovider2024 from '../../../content/media-library/awards/bba-best-childrens-provider.png';

const AwardsBanner = ({ hideHeading, order, customPadding }) => {
  const data = useStaticQuery(graphql`
    {
      allFile(filter: { relativePath: { regex: "/media-library/(awards/.+.(png|jpg)|logos/BBB-logo.jpg)/" } }) {
        nodes {
          absolutePath
          childImageSharp {
            gatsbyImageData(height: 96, quality: 90, layout: FIXED)
          }
        }
      }
    }
  `);

  let awards = {
    which: (
      <a
        key="which"
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.which.co.uk/money/banking/bank-accounts/best-bank-accounts/best-and-worst-banks-a8VTn0B0PJNC?utm_campaign=recommendedprovider&utm_medium=endorsement&utm_source=StarlingBank"
      >
        <img src={whichLogo} alt="Which? Recommended Provider logo for Current Accounts November 2024" />
      </a>
    ),
    timesmoneymentor: (
      <a
        key="timesmoneymentor"
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.thetimes.co.uk/money-mentor/banking-saving/times-money-mentor-awards-2023-the-winners"
      >
        <img src={times2023} alt="Time money mentor award for being the Best Current account 2023" />
      </a>
    ),
    bestbankingapp: (
      <a
        key="bestbankingapp"
        target="_blank"
        rel="noopener noreferrer"
        href="https://smartmoneypeople.com/british-bank-awards/winners"
      >
        <img src={bestapp2024} alt="British Bank award for Best banking app 2024" />
      </a>
    ),
    bestchildrensprovider: (
      <a
        key="bestchildrensprovider"
        target="_blank"
        rel="noopener noreferrer"
        href="https://smartmoneypeople.com/british-bank-awards/winners"
      >
        <img src={bestchildrensprovider2024} alt="British Bank award for Best children's provider 2024" />
      </a>
    ),
    trustpilot: <TrustBox key="trustpilot" template="53aa8807dec7e10d38f59f32" height="100px" hideReadMore />,
    fscs: (
      <div key="fscs">
        <img src={fscs} alt="FSCS logo" />
      </div>
    ),
    britishbusinessbank: (
      <a
        key="britishbusinessbank"
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.british-business-bank.co.uk/ourpartners/coronavirus-business-interruption-loan-schemes/"
      >
        <img src={fixedImage(data, '/logos/BBB-logo.jpg')?.images.fallback.src} alt="British Business Bank logo" />
      </a>
    ),
    finder: (
      <div key="finder">
        <img
          src={finderLogo}
          alt="Finder Awards 2023 logo - awarded to Starling Bank for Best Banking Customer Satisfaction"
        />
      </div>
    ),
    fairpayment: (
      <a
        key="fairpayment"
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.smallbusinesscommissioner.gov.uk/new-fair-payment-code/"
      >
        <img
          src={fairpaymentLogo}
          alt="Fair Payment Gold logo - awarded to Starling Bank for paying at least 95% of all invoices within 30 days"
        />
      </a>
    ),
    nacfb: (
      <div key="nacfb">
        <img src={nacfbLogo} alt="NACFB Logo" />
      </div>
    ),
    cass: (
      <div key="cass">
        <img src={cassLogo} alt="CASS Logo" />
      </div>
    ),
    contracting: (
      <div key="contracting">
        <img src={contracting} alt="Contracting Logo" />
      </div>
    ),
    startup: (
      <div key="startup">
        <img src={startupLogo} alt="Start Up Logo" />
      </div>
    ),
    gbea: (
      <div key="gbea">
        <img src={gbeaLogo} alt="GBEA Logo" />
      </div>
    ),
    luca: (
      <div key="luca">
        <img
          src={lucaLogo}
          alt="LUCA Awards logo - awarded to Starling Bank for being the winner of the Business Bank of the year 2021"
        />
      </div>
    )
  };

  const awardsImages = [
    {
      award: 'bestbritishbank',
      logo: '/awards/BestBritishBank-2020.png',
      alt: 'Badge awarded to Starling Bank for being the Best British Bank of 2021',
      link: 'https://www.britishbankawards.co.uk/'
    },
    {
      award: 'bestbritishcurrent',
      logo: '/awards/best-current-account-provider-2022.png',
      alt: 'A picture of badge awarded to Starling Bank for being the Best Current Account Provider 2022',
      link: 'https://www.britishbankawards.co.uk/'
    },
    {
      award: 'whichbankingbrand',
      logo: '/awards/Which-BANKING-BRAND-OF-THE-YEAR-2024.png',
      alt: 'A picture of badge awarded to Starling Bank for being Which Banking Brand of the Year 2024',
      link: 'https://www.which.co.uk/money/banking/bank-accounts/best-and-worst-banks-a3q5d8c6dj7y?utm_campaign=recommendedprovider&utm_medium=endorsement&utm_source=Starling%20Bank'
    },
    {
      award: 'bestbritishchildren',
      logo: '/awards/best-children-financial-provider.png',
      alt: 'A picture of badge awarded to Starling Bank for being the Best Children Financial Provider 2022',
      link: 'https://www.britishbankawards.co.uk/'
    },
    { award: 'finder-2020', logo: '/awards/finder-2020.png', alt: 'Finder award badge' }
  ];

  awardsImages.forEach((image) => {
    awards = {
      ...awards,
      [image.award]: (
        <div key={image.logo}>
          {image.link ? (
            <a href={image.link} target="_blank" rel="noopener noreferrer">
              <img src={fixedImage(data, image.logo)?.images.fallback.src} alt={image.alt} />
            </a>
          ) : (
            <img src={fixedImage(data, image.logo)?.images.fallback.src} alt={image.alt} />
          )}
        </div>
      )
    };
  });

  const componentClasses = classNames('awards-banner', {
    'awards-banner--no-padding-top': customPadding === 'no-top',
    'awards-banner--no-padding-bottom': customPadding === 'no-bottom',
    'awards-banner--no-padding-top-bottom': customPadding === 'no-padding'
  });

  return (
    <section className={componentClasses}>
      <div className="awards-banner__inner">
        {hideHeading === 'false' && <h5>Recognised for:</h5>}
        <div className="awards-banner__logos">{order.map((orderedAward) => awards[orderedAward])}</div>
      </div>
    </section>
  );
};

AwardsBanner.propTypes = {
  // fixme: known issue with proptypes
  hideHeading: PropTypes.string,
  order: PropTypes.array,
  customPadding: PropTypes.oneOf(['no-top', 'no-bottom', 'no-padding'])
};

AwardsBanner.defaultProps = {
  hideHeading: 'false',
  order: [
    'which',
    'britishbankawards',
    'finder',
    'bestbritishbank',
    'whichbankingbrand',
    'bestbritishcurrent',
    'bestbritishchildren',
    'trustpilot',
    'britishbusinessbank',
    'timesmoneymentor'
  ]
};

export default AwardsBanner;
